import React, { useState } from "react";
import { Printer } from "@phosphor-icons/react";
import { onlineTargets, pickTarget, setAgentConfig } from "@/lib/agent";
import { useT } from "@/lib/i18n";

// Mismo criterio que el agente para reconocer una Zebra (ZDesigner = driver de Zebra en Windows).
const LOOKS_LIKE_ZEBRA = /zebra|zpl|zdesigner/i;

/**
 * Elige en qué impresora (y en qué equipo, si hay varios) se imprime. La elección se recuerda en este
 * navegador y la leen los botones de imprimir con pickTarget(). "Automática" deja decidir al agente.
 */
export default function PrinterSelect({ agents, testId = "printer-select" }) {
    const t = useT();
    const [, bump] = useState(0);
    const targets = onlineTargets(agents);
    if (targets.length === 0) {
        return (
            <div data-testid={testId} className="text-sm text-brand-700 font-mono" translate="no">
                {t("printer.none")}
            </div>
        );
    }
    const current = pickTarget(agents);
    const several = new Set(targets.map((x) => x.agentId)).size > 1;
    const key = (x) => `${x.agentId}::${x.printer}`;
    const label = (x) => {
        const name = x.printer || (x.defaultPrinter ? t("printer.autoWith", { printer: x.defaultPrinter }) : t("printer.auto"));
        return several ? `${x.agentName} · ${name}` : name;
    };

    // Un solo destino posible: se muestra tal cual, sin desplegable.
    if (targets.length === 1) {
        return (
            <div data-testid={testId} className="bg-brand-50 border border-brand-200 px-3 py-2 text-sm font-mono text-brand-900 flex items-center gap-2" translate="no">
                <Printer size={16} /> <span>{`${t("printer.label")}: ${label(targets[0])}`}</span>
            </div>
        );
    }
    // Dónde saldrá realmente lo que se imprima: "Automática" se resuelve a la Zebra del equipo.
    const destination = current.printer || current.defaultPrinter;
    const suspicious = destination && !LOOKS_LIKE_ZEBRA.test(destination);
    return (
        <div translate="no">
        <div className="bg-brand-50 border border-brand-200 px-3 py-2 text-sm text-brand-900 flex items-center gap-2">
            <Printer size={16} />
            <label htmlFor={testId} className="font-medium whitespace-nowrap">{t("printer.choose")}:</label>
            <select
                id={testId}
                data-testid={testId}
                value={key(current)}
                onChange={(e) => {
                    const next = targets.find((x) => key(x) === e.target.value);
                    if (next) {
                        setAgentConfig({ agentId: next.agentId, printer: next.printer });
                        bump((n) => n + 1);
                    }
                }}
                className="flex-1 min-w-0 bg-white border border-brand-300 text-brand-950 text-sm rounded-none px-2 py-1 font-mono focus:outline-none focus:border-brand-900"
            >
                {targets.map((x) => (
                    <option key={key(x)} value={key(x)}>
                        {label(x)}
                    </option>
                ))}
            </select>
        </div>
        {destination && (
            <p data-testid={`${testId}-destination`} className="mt-1.5 px-1 text-sm font-mono text-brand-900">
                {`✓ ${t("printer.willPrint", { printer: several ? `${destination} (${current.agentName})` : destination })}`}
            </p>
        )}
        {suspicious && (
            <p data-testid={`${testId}-warning`} className="mt-1 px-3 py-1.5 text-sm bg-amber-50 border border-amber-300 text-amber-900">
                {`⚠ ${t("printer.notZebra", { printer: destination })}`}
            </p>
        )}
        </div>
    );
}
